import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Star, Clock, ChevronDown, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import { formatTL } from '@/lib/format';
import { Card, TierBadge, StatusBadge } from '@/components/shared';

const TIERS = ['Bronze', 'Silver', 'Gold', 'Diamond'];

const isUrl = (v) => typeof v === 'string' && /^(https?:\/\/|www\.|@|instagram\.com)/i.test(v.trim());
const hrefFor = (v) => {
  const s = (v || '').trim();
  if (s.startsWith('@')) return `https://instagram.com/${s.slice(1)}`;
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
};

const Detail = ({ label, value, link }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wide text-ink/40">{label}</p>
    {value ? (
      link && isUrl(value) ? (
        <a href={hrefFor(value)} target="_blank" rel="noreferrer"
          className="text-sm text-gold hover:underline inline-flex items-center gap-1 truncate">
          {value} <ExternalLink strokeWidth={1.5} className="w-3 h-3 shrink-0" />
        </a>
      ) : (
        <p className="text-sm text-ink truncate">{value}</p>
      )
    ) : (
      <p className="text-sm text-ink/30">—</p>
    )}
  </div>
);

export default function AdminArchitects() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRow, setOpenRow] = useState(null);

  const load = () => api.get('/admin/architects').then((r) => setRows(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    try {
      await api.put(`/admin/architects/${id}`, patch);
      toast.success('Güncellendi');
      load();
    } catch { toast.error('Güncelleme başarısız'); }
  };

  const setAotm = async (id) => {
    try { await api.post('/admin/ayin-mimari', { architectId: id }); toast.success('Ayın Mimarı seçildi'); }
    catch { toast.error('İşlem başarısız'); }
  };

  if (loading) return <div className="text-navy/40 text-sm">Yükleniyor...</div>;

  const pending = rows.filter((a) => !a.approved);

  return (
    <div className="fade-up" data-testid="admin-architects">
      <h1 className="font-serif text-3xl text-navy mb-1">Mimarlar</h1>
      <p className="text-navy/50 text-sm mb-6">Onay, seviye ve durum yönetimi</p>

      {pending.length > 0 && (
        <div className="mb-6 rounded-2xl border border-gold/40 bg-gold/10 p-5" data-testid="pending-approvals">
          <div className="flex items-center gap-2 mb-3">
            <Clock strokeWidth={1.5} className="w-4 h-4 text-gold" />
            <p className="text-sm font-semibold text-navy">Onay bekleyen {pending.length} mimar</p>
          </div>
          <div className="space-y-2">
            {pending.map((a) => (
              <div key={a.id} className="bg-surface border border-line rounded-xl px-4 py-2.5" data-testid={`pending-row-${a.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <button
                    onClick={() => setOpenRow(openRow === a.id ? null : a.id)}
                    data-testid={`pending-detail-toggle-${a.id}`}
                    className="min-w-0 text-left flex-1"
                  >
                    <p className="text-sm font-medium text-ink truncate flex items-center gap-1.5">
                      {a.name}
                      <ChevronDown strokeWidth={1.5} className={`w-3.5 h-3.5 text-ink/40 transition-transform ${openRow === a.id ? 'rotate-180' : ''}`} />
                    </p>
                    <p className="text-xs text-ink/50 truncate">{a.email}{a.city ? ` · ${a.city}` : ''}</p>
                  </button>
                  <button onClick={() => update(a.id, { approved: true })} data-testid={`approve-pending-${a.id}`}
                    className="inline-flex items-center gap-1 text-xs bg-greenx text-cream px-3 py-1.5 rounded-lg active:scale-95 transition-transform shrink-0">
                    <Check strokeWidth={1.5} className="w-3.5 h-3.5" /> Onayla
                  </button>
                </div>
                {openRow === a.id && (
                  <div className="mt-3 pt-3 border-t border-line" data-testid={`pending-detail-${a.id}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                      <Detail label="Telefon" value={a.phone} />
                      <Detail label="Şehir" value={a.city} />
                      <Detail label="Firma / Ofis" value={a.company} />
                      <Detail label="Üniversite" value={a.university} />
                      <Detail label="Bölüm" value={a.department} />
                      <Detail label="Web / Instagram" value={a.website} link />
                      <Detail label="Nereden duydu" value={a.hearAbout} />
                    </div>
                    {isUrl(a.website) && (
                      <a
                        href={hrefFor(a.website)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`view-work-${a.id}`}
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-navy border border-navy/25 hover:bg-navy hover:text-cream px-3 py-1.5 rounded-lg transition-colors"
                      >
                        <ExternalLink strokeWidth={1.5} className="w-3.5 h-3.5" /> İşlerini Gör
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-navy/50 text-xs border-b border-navy/10">
              <th className="py-3 pr-4 font-medium">Mimar</th>
              <th className="py-3 pr-4 font-medium">Şehir</th>
              <th className="py-3 pr-4 font-medium">Bu ay ciro</th>
              <th className="py-3 pr-4 font-medium">Seviye</th>
              <th className="py-3 pr-4 font-medium">Durum</th>
              <th className="py-3 pr-4 font-medium">İşlemler</th>
            </tr>
          </thead>
          <tbody data-testid="architects-table">
            {rows.map((a) => (
              <tr key={a.id} className="border-b border-navy/10 align-middle">
                <td className="py-3 pr-4">
                  <p className="text-navy font-medium">{a.name}</p>
                  <p className="text-xs text-navy/40">{a.email}</p>
                </td>
                <td className="py-3 pr-4 text-navy/70">{a.city || '-'}</td>
                <td className="py-3 pr-4 text-navy/70">{formatTL(a.turnoverMonth)}</td>
                <td className="py-3 pr-4">
                  <select
                    value={a.tier}
                    onChange={(e) => update(a.id, { tier: e.target.value })}
                    data-testid={`tier-select-${a.id}`}
                    className="bg-cream border border-navy/15 rounded-md px-2 py-1 text-xs text-navy focus:outline-none"
                  >
                    {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </td>
                <td className="py-3 pr-4">
                  {a.approved ? <StatusBadge status={a.status} /> : <span className="text-xs text-gold">Onay bekliyor</span>}
                </td>
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-2">
                    {!a.approved && (
                      <button onClick={() => update(a.id, { approved: true })} data-testid={`approve-${a.id}`} className="inline-flex items-center gap-1 text-xs bg-greenx text-cream px-2.5 py-1.5 rounded-md active:scale-95 transition-transform">
                        <Check strokeWidth={1.5} className="w-3.5 h-3.5" /> Onayla
                      </button>
                    )}
                    <button
                      onClick={() => update(a.id, { status: a.status === 'active' ? 'passive' : 'active' })}
                      data-testid={`toggle-status-${a.id}`}
                      className="text-xs border border-navy/20 text-navy px-2.5 py-1.5 rounded-md active:scale-95 transition-transform"
                    >
                      {a.status === 'active' ? 'Pasifleştir' : 'Aktifleştir'}
                    </button>
                    <button onClick={() => setAotm(a.id)} data-testid={`aotm-${a.id}`} className="inline-flex items-center gap-1 text-xs text-gold border border-gold/30 px-2.5 py-1.5 rounded-md active:scale-95 transition-transform">
                      <Star strokeWidth={1.5} className="w-3.5 h-3.5" /> Ayın Mimarı
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
